import {
  Breadcrumbs,
  Button,
  Link as MUILink,
  styled,
  Typography,
} from "@mui/material";
import TopNav from "../components/TopNav";
import { Link, useSearchParams } from "react-router-dom";
import { Image as KImage, Layer, Line, Stage } from "react-konva";
import { useEffect, useRef, useState } from "react";
import pageStyle from "./PhotoPage.module.css";
import { getPhoto, savePhoto } from "../api/photos";
import { Photo } from "@mui/icons-material";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function PhotosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const cid = searchParams.get("cid");

  const [isModified, setIsModified] = useState(false);

  const [kImg, setKImg] = useState<any>(null);
  const imageObj = new Image();

  const loadImage = () => {
    if (!imageObj) return;
    setKImg(imageObj);
  };

  imageObj.onload = loadImage;
  imageObj.onchange = loadImage;

  useEffect(() => {
    if (!imageObj) return;
    getPhoto(id || undefined).then((photo) => {
      const file = photo.photoData;
      if (!file) return;
      imageObj.src = file;
      setLines([]);
      setSearchParams((prev) => {
        prev.set("cid", photo.customerID);
        return prev;
      });
    });
  }, [id]);

  const tool = "pen";
  const [lines, setLines] = useState<{ tool: string; points: number[] }[]>([]);
  const isDrawing = useRef(false);
  const stageRef = useRef<any>(null);

  const handleMouseDown = (e: any) => {
    isDrawing.current = true;
    const pos = e.target.getStage().getPointerPosition();
    setLines([...lines, { tool, points: [pos.x, pos.y] }]);
  };

  const handleMouseMove = (e: any) => {
    // no drawing - skipping
    if (!isDrawing.current) {
      return;
    }
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    let lastLine = lines[lines.length - 1];
    // add point
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    // replace last
    lines.splice(lines.length - 1, 1, lastLine);
    setLines(lines.concat());
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const canSave = () => {
    return isModified || lines.length != 0;
  };

  const uploadImage = (e: any) => {
    setIsModified(true);
    if (!imageObj) return;
    const file = e.target.files[0];
    const url = URL.createObjectURL(file);
    imageObj.src = url;
    setLines([]);
  };

  const saveImage = async () => {
    const uri = stageRef.current.toDataURL();

    const photo = await savePhoto({
      id: id || undefined,
      photoData: uri,
      customerID: cid || "",
    });
    const file = photo.photoData;
    if (!file) return;
    imageObj.src = file;
    setSearchParams((sp) => {
      sp.set("id", photo.id || "");
      return sp;
    });

    setLines([]);
    setIsModified(false);
  };

  return (
    <div className="page">
      <TopNav />
      <main className={pageStyle.main}>
        <Breadcrumbs>
          <MUILink
            component={Link}
            underline="hover"
            color="inherit"
            to="/customers"
          >
            Customers
          </MUILink>
          <MUILink
            component={Link}
            underline="hover"
            color="inherit"
            to={`/customers?id=${cid}`}
          >
            Details
          </MUILink>
          <Typography color="text.primary">Photo</Typography>
        </Breadcrumbs>
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<Photo />}
        >
          Add Photo
          <VisuallyHiddenInput type="file" onChange={uploadImage} multiple />
        </Button>
        <div className={pageStyle.canvasContainerContainer}>
          <div id="canvasContainer" className={pageStyle.canvasContainer}>
            <Stage
              width={document.getElementById("canvasContainer")?.scrollWidth}
              height={document.getElementById("canvasContainer")?.scrollHeight}
              onMouseDown={handleMouseDown}
              onMousemove={handleMouseMove}
              onMouseup={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              ref={stageRef}
            >
              <Layer>
                <KImage
                  image={kImg}
                  width={
                    document.getElementById("canvasContainer")?.scrollWidth
                  }
                  height={
                    document.getElementById("canvasContainer")?.scrollHeight
                  }
                />
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke="#df4b26"
                    strokeWidth={5}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={
                      line.tool === "eraser" ? "destination-out" : "source-over"
                    }
                  />
                ))}
              </Layer>
            </Stage>
          </div>
        </div>
        <Button
          disabled={!canSave()}
          variant="contained"
          color="success"
          onClick={saveImage}
        >
          Save
        </Button>
      </main>
    </div>
  );
}

export default PhotosPage;
